import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { ShieldAlert } from "lucide-react";

import { billingGetTierInfo } from "~/api-refs";
import { PageWrapper } from "~/components/page-wrapper";
import { BillingContent } from "~/components/settings/billing/billing-content";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { Spinner } from "~/components/ui/spinner";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";

export const Route = createFileRoute("/_authenticated/settings/billing")({
  component: BillingPage,
  pendingComponent: () => (
    <PageWrapper
      description="Manage your billing tier and payment method"
      title="Billing"
    >
      <div className="flex items-center justify-center p-12">
        <Spinner className="size-8" />
      </div>
    </PageWrapper>
  ),
});

function BillingPage() {
  const { organization } = useActiveOrganization();
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  const tierInfo = useQuery(
    billingGetTierInfo,
    canViewIntegrations ? {} : "skip"
  );

  if (!organization) {
    return null;
  }

  if (permissionsLoading) {
    return (
      <BillingPageWrapper>
        <Skeleton className="h-96 w-full" />
      </BillingPageWrapper>
    );
  }

  if (!hasEmployee) {
    return (
      <BillingPageWrapper>
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </BillingPageWrapper>
    );
  }

  if (!canViewIntegrations) {
    return (
      <BillingPageWrapper>
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view billing settings. Contact your
            manager to request access.
          </AlertDescription>
        </Alert>
      </BillingPageWrapper>
    );
  }

  if (!tierInfo) {
    return (
      <BillingPageWrapper>
        <div className="flex items-center justify-center p-12">
          <Spinner className="size-8" />
        </div>
      </BillingPageWrapper>
    );
  }

  return (
    <BillingPageWrapper>
      <BillingContent organization={organization} tierInfo={tierInfo} />
    </BillingPageWrapper>
  );
}

function BillingPageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <PageWrapper
      description="Manage your billing tier and payment method"
      title="Billing"
    >
      {children}
    </PageWrapper>
  );
}
